import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { fmtMoneyWithDecimals, pad } from '../../utils/formatters';
import { reorderColumns } from '../../lib/columnReorder';
import { useAuthoritativeTradesRecords } from '../../hooks/useAuthoritativeTrades';
import {
  getTradeDateKey,
  getTradePnl,
  getTradeVolume,
  normalizeTradeSide,
  normalizeTradeStatus,
  type AnyTrade,
} from '../../lib/trades/authoritativeTradeData';
import { readTradingSettings } from '../../config/tradingSettingsDefaults';
import { resolveTradeInitialRiskValue } from '../../lib/trades/tradeInitialRisk';
import {
  RECENT_TRADES_DEFAULT_COLUMN_ORDER,
  sanitizeRecentTradesColumnVisibility,
  sanitizeRecentTradesColumnOrder,
} from '../../config/recentTradesColumns';
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';
import { filterCanonicalDisplayTrades } from '../../lib/trades/dashboardTradeSource';
import { getTradeNotionalVolume } from '../../lib/trades/dashboardBucketAnalytics';

interface OpenTrade {
  id: number;
  openDate: string;
  openDateKey: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  volume: number;
  execs: number;
  pnl: number;
  notes: string;
  setup: string;
  tags: string[];
  sortStamp: number;
  pnlPercentEquities: number | null;
  pnlTicksGrossFutures: number | null;
  pnlTicksPerContractGrossFutures: number | null;
  commission: number | null;
  commissionAndFees: number | null;
  initialRisk: number | null;
  pnlR: number | null;
  status: 'open' | 'closed';
}

const toFiniteNumber = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getTradeExecutionCount = (trade: Record<string, any>): number => {
  // Persisted trades can include `executions` as an array as well as numeric
  // count fields. Prefer the explicit aggregate count; coercing an empty array
  // with Number([]) incorrectly displayed zero fills even when count data exists.
  const explicitCount = trade?.executionCount ?? trade?.execution_count;
  if (explicitCount != null && explicitCount !== '') {
    const count = Number(explicitCount);
    if (Number.isFinite(count) && count >= 0) return Math.floor(count);
  }

  if (Array.isArray(trade?.executions)) return trade.executions.length;
  if (Array.isArray(trade?.executionRows)) return trade.executionRows.length;
  if (Array.isArray(trade?.fills)) return trade.fills.length;

  const count = Number(trade?.executions ?? trade?.fills ?? 0);
  return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
};

const formatOpenDate = (trade: Record<string, any>) => {
  const dateKey = getTradeDateKey(trade as any);
  if (!dateKey) return '—';
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (!Number.isFinite(parsed.getTime())) return '—';
  return `${pad(parsed.getDate())} ${parsed.toLocaleString('default',{month:'short'})} ${parsed.getFullYear()}`;
};

type ColDef = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  isNumeric?: boolean;
  width?: string;
  pinned?: 'left' | 'right';
  render?: (t: OpenTrade) => React.ReactNode;
};

export const RecentTradesWidget: React.FC<any> = ({ page = 0, setPage = () => {}, visibleColumns, onLimitBannerChange, trades: providedTrades }) => {
  const fallbackAuthoritativeTrades = useAuthoritativeTradesRecords();
  const authoritativeTrades = Array.isArray(providedTrades) ? providedTrades : fallbackAuthoritativeTrades;
  const displayTrades = useMemo(
    () => filterCanonicalDisplayTrades(authoritativeTrades),
    [authoritativeTrades],
  );
  const defaultRiskPerTrade = readTradingSettings().riskRules.defaultRiskPerTrade;
  const [assetClassVisibility] = useAssetClassVisibility();
  const isStocksOnly = assetClassVisibility.STOCKS && !assetClassVisibility.FUTURES && !assetClassVisibility.FOREX && !assetClassVisibility.CRYPTO;
  const effectiveVisibleColumns = useMemo(
    () => sanitizeRecentTradesColumnVisibility(visibleColumns),
    [visibleColumns]
  );
  const trades = useMemo<OpenTrade[]>(() => {
    return displayTrades.map((trade: any, index: number) => {
      const commission = toFiniteNumber(trade?.commission);
      const fees = toFiniteNumber(trade?.fees ?? trade?.fee);
      const initialRisk = resolveTradeInitialRiskValue(trade, defaultRiskPerTrade);
      const pnl = getTradePnl(trade);
      const explicitPnlPercent = toFiniteNumber(trade?.pnlPercentEquities);
      const tradeNotional = getTradeNotionalVolume(trade);
      const pnlPercentEquities = explicitPnlPercent ?? (
        normalizeTradeStatus(trade) === 'closed' && tradeNotional != null && tradeNotional > 0
          ? (pnl / tradeNotional) * 100
          : null
      );
      const openDateKey = getTradeDateKey(trade as any) || '';
      const sortStamp = Number(
        trade?.openTimestampMs
        ?? trade?.timestamp
        ?? trade?.createdAt
        ?? (openDateKey ? Date.parse(`${openDateKey}T00:00:00`) : null)
        ?? trade?.date
        ?? trade?.id
        ?? index + 1
      );
      const numericId = Number(trade?.id ?? trade?.timestamp ?? trade?.openTimestampMs ?? index + 1);

      return {
        id: Number.isFinite(numericId) ? numericId : index + 1,
        openDate: formatOpenDate(trade),
        openDateKey,
        symbol: String(trade?.symbol ?? '—'),
        side: normalizeTradeSide(trade) === 'SHORT' ? 'SHORT' : 'LONG',
        volume: getTradeVolume(trade),
        execs: getTradeExecutionCount(trade),
        pnl,
        notes: String(trade?.notes ?? ''),
        setup: String(trade?.setup ?? trade?.strategy ?? trade?.setupName ?? trade?.notes ?? ''),
        tags: Array.isArray(trade?.tags) ? trade.tags : [],
        sortStamp: Number.isFinite(sortStamp) ? sortStamp : numericId,
        pnlPercentEquities,
        pnlTicksGrossFutures: toFiniteNumber(trade?.pnlTicksGrossFutures),
        pnlTicksPerContractGrossFutures: toFiniteNumber(trade?.pnlTicksPerContractGrossFutures),
        commission,
        commissionAndFees: commission != null || fees != null ? (commission ?? 0) + (fees ?? 0) : null,
        initialRisk,
        pnlR: toFiniteNumber(trade?.pnlR),
        status: normalizeTradeStatus(trade),
      };
    });
  }, [defaultRiskPerTrade, displayTrades]);
  const latestTradingDayTrades = useMemo(() => {
    const sortedTrades = [...trades].sort((left, right) => {
      const delta = Number(right.sortStamp ?? 0) - Number(left.sortStamp ?? 0);
      if (delta !== 0) return delta;
      return Number(right.id ?? 0) - Number(left.id ?? 0);
    });
    const recentDateKeys = new Set<string>();

    for (const trade of sortedTrades) {
      const dateKey = trade.openDateKey || trade.openDate;
      if (!dateKey || dateKey === '—') continue;
      recentDateKeys.add(dateKey);
      if (recentDateKeys.size >= 5) break;
    }

    return sortedTrades.filter((trade) => recentDateKeys.has(trade.openDateKey || trade.openDate));
  }, [trades]);
  const tradingDateCaption = useMemo(() => {
    const dateKeys = [...new Set(latestTradingDayTrades.map((trade) => trade.openDateKey).filter(Boolean))].sort();
    if (dateKeys.length === 0) return 'Most recent trading days';
    if (dateKeys.length === 1) return `Most recent trading day: ${dateKeys[0]}`;
    return `Most recent ${dateKeys.length} trading days: ${dateKeys[0]} – ${dateKeys[dateKeys.length - 1]}`;
  }, [latestTradingDayTrades]);

  const rowsPerPage = 10;
  const pageCount = Math.max(1, Math.ceil(latestTradingDayTrades.length / rowsPerPage));
  const currentPage = Math.min(Math.max(Number(page) || 0, 0), pageCount - 1);
  const paginatedTrades = latestTradingDayTrades.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    if (latestTradingDayTrades.length === 0) {
      if (currentPage !== 0) setPage(0);
      return;
    }
    if (currentPage !== page) setPage(currentPage);
  }, [currentPage, latestTradingDayTrades.length, page, setPage]);

  const show = (key: string) => !!(effectiveVisibleColumns as Record<string, boolean>)[key];

  const baseDefs: Record<string,ColDef> = {
    status:{ key:'status',label:'Status',align:'center',width:'120px',render:t=>{
      const statusLower = String(t.status || '').trim().toLowerCase();
      const isOpen = statusLower === 'open';
      const displayStatus = isOpen ? 'OPEN' : 'CLOSED';
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-semibold rounded-full ring-1 min-w-[92px] justify-center ${
            isOpen
              ? 'bg-emerald-50 text-emerald-600 ring-emerald-200/70 dark:bg-transparent dark:text-emerald-400 dark:ring-emerald-500/50'
              : 'bg-zinc-50 text-zinc-700 ring-zinc-200/70 dark:bg-transparent dark:text-zinc-200 dark:ring-zinc-600/60'
          }`}
        >
          <span
            className={
              isOpen
                ? 'w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400'
                : 'w-1.5 h-1.5 rounded-full bg-zinc-500 dark:bg-white'
            }
          />
          {displayStatus}
        </span>
      );
    }},
    openDate:{key:'openDate',label:'Open Date',align:'center',width:'150px',render:t=>t.openDate},
    symbol:{key:'symbol',label:'Symbol',align:'center',width:'120px',render:t=><span className="font-medium">{t.symbol}</span>},
    side:{key:'side',label:'Side',align:'center',width:'100px',render:t=>(
      <span className={`inline-flex items-center justify-center gap-1 w-20 p-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${t.side === 'LONG'
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700'
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700'}`}>
        {t.side === 'LONG' ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
        <span className="uppercase text-[11px] tracking-wide leading-none">{t.side}</span>
      </span>
    )},
    volume:{key:'volume',label:isStocksOnly ? 'Shares' : 'Quantity',align:'center',isNumeric:true,width:'120px',render:t=>t.volume.toLocaleString()},
    execs:{key:'execs',label:'Fills',align:'center',isNumeric:true,width:'100px',render:t=>t.execs},
    tags:{key:'tags',label:'Tags',align:'center',width:'150px',render:t=>t.tags.join(', ')},
    notes:{key:'notes',label:'Setup / Notes',align:'center',width:'200px',render:t=>t.setup || t.notes || <span className="text-zinc-400">—</span>},
    pnlPercentEquities:{key:'pnlPercentEquities',label:'P&L (%)',align:'center',isNumeric:true,width:'120px',render:t=>t.pnlPercentEquities===null?<span className="text-zinc-400">—</span>:<span className={`${t.pnlPercentEquities>=0?'text-emerald-600':'text-red-600'}`}>{t.pnlPercentEquities.toFixed(2)}%</span>},
    pnl:{key:'pnl',label:'P&L',align:'center',isNumeric:true,width:'150px',render:t=>(
      normalizeTradeStatus(t as unknown as AnyTrade) === 'open' ? (
        <span className="text-zinc-400">—</span>
      ) : (
        <span className={`inline-flex items-center gap-1 ${t.pnl>=0?'text-emerald-600':'text-red-600'}`}>
          <span className="text-[12px]">{t.pnl>=0?'▲':'▼'}</span>
          {fmtMoneyWithDecimals(t.pnl,2)}
        </span>
      )
    )},
    pnlTicksGrossFutures:{key:'pnlTicksGrossFutures',label:'P&L (ticks)',align:'center',isNumeric:true,width:'150px',render:t=>t.pnlTicksGrossFutures===null?<span className="text-zinc-400">—</span>:t.pnlTicksGrossFutures},
    pnlTicksPerContractGrossFutures:{key:'pnlTicksPerContractGrossFutures',label:'P&L (ticks/contract)',align:'center',isNumeric:true,width:'220px',render:t=>t.pnlTicksPerContractGrossFutures===null?<span className="text-zinc-400">—</span>:t.pnlTicksPerContractGrossFutures},
    commission:{key:'commission',label:'Commission',align:'center',isNumeric:true,width:'150px',render:t=>t.commission?.toFixed(2)||<span className="text-zinc-400">—</span>},
    commissionAndFees:{key:'commissionAndFees',label:'Commission + Fees',align:'center',isNumeric:true,width:'200px',render:t=>t.commissionAndFees?.toFixed(2)||<span className="text-zinc-400">—</span>},
    initialRisk:{key:'initialRisk',label:'Initial Risk',align:'center',isNumeric:true,width:'150px',render:t=>t.initialRisk?.toFixed(2)||<span className="text-zinc-400">—</span>},
    pnlR:{key:'pnlR',label:'P&L (R)',align:'center',isNumeric:true,width:'120px',render:t=>t.pnlR===null?<span className="text-zinc-400">—</span>:t.pnlR},
    shared:{key:'shared',label:'Shared',align:'center',width:'120px',render:_t=>null},
  };

  const defaultOrder = RECENT_TRADES_DEFAULT_COLUMN_ORDER.filter((key) => baseDefs[key]);
  const STORAGE_KEY = 'openTradesColumnOrder';
  const WIDTHS_KEY = 'openTradesColumnWidths';

  // Horizontal offset (px) for the left/right chevron move buttons from the cell edge.
  // Keep this as a single source of truth so header and rows stay consistent.
  const CHEVRON_OFFSET = 4; // 4px from each side
  // Exact gap (px) between the chevron button and the header label text.
  // const CHEVRON_LABEL_GAP = 12; // px (removed; no longer used after header centering)

  // Helper to parse string widths like '7rem' or '120px' to px numbers
  const parseWidthToPx = (w?: string): number | undefined => {
    if (!w) return undefined;
    const trimmed = String(w).trim();
    if (trimmed.endsWith('px')) {
      const n = parseFloat(trimmed.replace('px',''));
      return isFinite(n) ? n : undefined;
    }
    if (trimmed.endsWith('rem')) {
      const n = parseFloat(trimmed.replace('rem',''));
      return isFinite(n) ? Math.round(n * 16) : undefined; // assume 16px base
    }
    const n = parseFloat(trimmed);
    return isFinite(n) ? n : undefined;
  };
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return sanitizeRecentTradesColumnOrder(parsed).filter((key) => baseDefs[key]);
      }
    } catch {}
    return defaultOrder;
  });

  // (removed duplicate columnWidths state declaration)


  // Column widths (px), persisted
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(WIDTHS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return parsed as Record<string, number>;
      }
    } catch {}
    // derive defaults from baseDefs.width
    const init: Record<string, number> = {};
    for (const key of defaultOrder) {
      const px = parseWidthToPx((baseDefs as any)[key]?.width);
      if (px) init[key] = px;
    }
    return init;
  });

  useEffect(() => {
    localStorage.setItem(WIDTHS_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem(WIDTHS_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    const sanitized = sanitizeRecentTradesColumnOrder(columnOrder).filter((key) => baseDefs[key]);
    if (sanitized.length !== columnOrder.length || sanitized.some((key, index) => key !== columnOrder[index])) {
      setColumnOrder(sanitized);
    }
  }, []);

  // Fixed width helpers: default width, independently configurable minimum
  // width, and current effective width.
  const MIN_COLUMN_PX_DEFAULT = 80;
  const MIN_COLUMN_PX_OVERRIDES: Record<string, number> = {
    openDate: 110,
    symbol: 90,
    side: 80,
    volume: 90,
    execs: 76,
    notes: 140,
    pnlPercentEquities: 90,
    pnl: 100,
    status: 110,
    commission: 80,
  };
  const getBaseWidthPx = (key: string): number => {
    const px = parseWidthToPx((baseDefs as any)[key]?.width);
    if (typeof px === 'number' && isFinite(px)) return px;
    // If no explicit width, estimate a useful initial width from the label.
    const lbl = (baseDefs as any)[key]?.label || String(key);
    return Math.min(400, Math.max(100, lbl.length * 10 + 24));
  };
  const getMinWidthPx = (key: string): number => {
    const override = MIN_COLUMN_PX_OVERRIDES[key];
    if (typeof override === 'number') return override;
    const baseWidth = getBaseWidthPx(key);
    const label = String((baseDefs as any)[key]?.label || key);
    return Math.min(baseWidth, Math.max(MIN_COLUMN_PX_DEFAULT, label.length * 7 + 48));
  };
  const getWidthForKey = (key: string): number => {
    const w = columnWidths[key];
    return typeof w === 'number' && isFinite(w) ? w : getBaseWidthPx(key);
  };

  const visibleKeys = columnOrder.filter((key) => baseDefs[key] && show(key));

  const columns = visibleKeys.map((key) => baseDefs[key]);
  const renderedColumnKeys = columns.map((col) => col.key);

  // Scroll container for auto-scroll
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Track container width to evenly distribute columns across available space
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Observe container width changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const RO: any = (window as any).ResizeObserver;
    const ro = RO ? new RO(() => update()) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Inject CSS to visually hide the horizontal scrollbar while preserving scroll behavior
  useEffect(() => {
    const id = 'open-trades-scroll-hide';
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = id;
      document.head.appendChild(el);
    }
    el.innerHTML = `
      .open-trades-scroll::-webkit-scrollbar { display: none; }
      .open-trades-scroll { -ms-overflow-style: none; scrollbar-width: none; }
    `;
    return () => {
      const e = document.getElementById(id);
      if (e) e.remove();
    };
  }, []);

  // Helper to reorder respecting pinned and hidden columns
  function reorderAllColumns(order: string[], fromKey: string, toKey: string, visibleOrder: string[]): string[] {
    const effectiveVisible = visibleOrder && visibleOrder.length ? visibleOrder : columns.map(c => c.key);
    const pinnedLeft = order.filter(k => (baseDefs as any)[k]?.pinned === 'left');
    const pinnedRight = order.filter(k => (baseDefs as any)[k]?.pinned === 'right');
    return reorderColumns(
      {
        visible: effectiveVisible,
        fullOrder: order,
        pinnedLeft,
        pinnedRight,
      },
      fromKey,
      toKey
    );
  }

  const handleColumnShift = (fromKey: string, direction: -1 | 1, orderSnapshot: string[]) => {
    if (!orderSnapshot || !orderSnapshot.length) return;
    const currentIndex = orderSnapshot.indexOf(fromKey);
    if (currentIndex === -1) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= orderSnapshot.length) return;
    const targetKey = orderSnapshot[targetIndex];
    if (!targetKey || targetKey === fromKey) return;
    setColumnOrder(prev => reorderAllColumns(prev, fromKey, targetKey, orderSnapshot));
  };

  // Header cell with chevron move + resize
  function HeaderCell({ c, idx, visibleOrder, onShift }: { c: ColDef; idx: number; visibleOrder: string[]; onShift: (fromKey: string, direction: -1 | 1, orderSnapshot: string[]) => void }) {
    const startResize = (ev: React.MouseEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      const startX = (ev as React.MouseEvent).clientX;
      const startWidth = getWidthForKey(c.key);
      const minWidth = getMinWidthPx(c.key);
      const onMove = (me: MouseEvent) => {
        const dx = me.clientX - startX;
        const raw = Math.round(startWidth + dx);
        const next = Number.isFinite(raw) ? Math.max(minWidth, raw) : minWidth;
        setColumnWidths(prev => ({ ...prev, [c.key]: next }));
      };
      const end = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', end);
      };
      window.addEventListener('mousemove', onMove, { passive: true });
      window.addEventListener('mouseup', end, { passive: true });
    };

    const currentIndex = visibleOrder.indexOf(c.key);
    const canMoveLeft = currentIndex > 0;
    const canMoveRight = currentIndex !== -1 && currentIndex < visibleOrder.length - 1;
    const isLastVisible = currentIndex === visibleOrder.length - 1;

    const moveColumnLeft = (ev: React.MouseEvent) => {
      ev.stopPropagation();
      if (!canMoveLeft) return;
      onShift(c.key, -1, visibleOrder.slice());
    };

    const moveColumnRight = (ev: React.MouseEvent) => {
      ev.stopPropagation();
      if (!canMoveRight) return;
      onShift(c.key, 1, visibleOrder.slice());
    };

    return (
      <th
        aria-colindex={idx + 1}
        style={{
          transition: 'background-color 120ms ease',
          minWidth: getWidthForKey(c.key),
        }}
        className={`py-3 pl-2 pr-1 select-none text-[13px] font-bold text-zinc-700 dark:text-zinc-300 relative overflow-hidden rounded-sm ${c.align==='right'? 'text-right': c.align==='center'? 'text-center':'text-left'}`}
        data-col-key={c.key}
      >
        <div className="absolute inset-y-0 z-20 pointer-events-auto flex items-center" style={{ left: CHEVRON_OFFSET }}>
          <button
            type="button"
            aria-label={`Move ${c.label} left`}
            className="flex h-10 w-[30px] items-center justify-center rounded border-0 bg-transparent p-0 hover:bg-blue-600 focus:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 active:bg-blue-700 disabled:opacity-40 dark:hover:bg-blue-600 dark:focus:bg-blue-600 dark:active:bg-blue-700"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onClick={moveColumnLeft}
            disabled={!canMoveLeft}
            aria-disabled={!canMoveLeft}
            style={{ lineHeight: 0 }}
          >
            <ChevronLeft className="w-4 h-4 text-zinc-700 dark:text-zinc-200 pointer-events-none m-0" aria-hidden="true" />
          </button>
        </div>
        <div className="absolute inset-y-0 z-20 pointer-events-auto flex items-center" style={{ right: CHEVRON_OFFSET }}>
          <button
            type="button"
            aria-label={`Move ${c.label} right`}
            className="flex h-10 w-[30px] items-center justify-center rounded border-0 bg-transparent p-0 hover:bg-blue-600 focus:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 active:bg-blue-700 disabled:opacity-40 dark:hover:bg-blue-600 dark:focus:bg-blue-600 dark:active:bg-blue-700"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onClick={moveColumnRight}
            disabled={!canMoveRight}
            aria-disabled={!canMoveRight}
            style={{ lineHeight: 0 }}
          >
            <ChevronRight className="w-4 h-4 text-zinc-700 dark:text-zinc-200 pointer-events-none m-0" aria-hidden="true" />
          </button>
        </div>
        <div className="absolute left-1/2 top-0 bottom-0 flex items-center justify-center pointer-events-none" style={{ transform: 'translateX(-50%)' }}>
          <span className="font-bold text-center select-none header-label whitespace-nowrap px-2">{c.label}</span>
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${c.label} column`}
          {...(!isLastVisible ? { title: 'Drag to resize column' } : {})}
          className="absolute right-0 top-0 h-full w-4 cursor-col-resize select-none group/handle pointer-events-auto z-30"
          onMouseDown={startResize}
          onPointerDown={(e) => { e.stopPropagation(); }}
        >
          {!isLastVisible && (
            <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
              <div className="w-px h-5 bg-blue-200 dark:bg-blue-300" />
              <div className="w-px h-5 bg-blue-200 dark:bg-blue-300 ml-0.5" />
            </div>
          )}
        </div>
      </th>
    );
  }

  // Compute equal distribution widths (respecting per-column min widths) to fill container
  const computedWidths = React.useMemo(() => {
    const keys = columns.map(c => c.key);
    if (!keys.length) return {} as Record<string, number>;
    const mins = keys.map(k => getWidthForKey(k));
    const minSum = mins.reduce((a, b) => a + b, 0);
    const avail = containerWidth || 0;
    if (avail <= minSum) {
      // Not enough space: use min widths; table overflows horizontally, no overlap
      return keys.reduce((acc, k, i) => { acc[k] = mins[i]; return acc; }, {} as Record<string, number>);
    }
    // Distribute extra space evenly
    const extra = avail - minSum;
    const per = Math.floor(extra / keys.length);
    let rem = extra - per * keys.length;
    const out: Record<string, number> = {};
    keys.forEach((k, i) => {
      const grow = per + (rem > 0 ? 1 : 0);
      if (rem > 0) rem -= 1;
      out[k] = mins[i] + grow;
    });
    return out;
  }, [columns.map(c => c.key).join(','), columnWidths, containerWidth]);

  // compute table min width once so other elements (notification bar) can match it
  const tableMinWidth = columns.reduce((sum, c) => sum + (computedWidths[c.key] ?? getWidthForKey(c.key)), 0);

  useEffect(() => {
    onLimitBannerChange?.(null);
    return () => {
      onLimitBannerChange?.(null);
    };
  }, [onLimitBannerChange]);

  return (
    <div className="h-full flex flex-col">
      <div
        ref={scrollRef}
        role="region"
        aria-label="Recent trades table"
        tabIndex={0}
        className="open-trades-scroll min-w-0 max-w-full touch-pan-x flex-grow overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-lg border border-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-zinc-700"
      >
        {latestTradingDayTrades.length > 0 ? (
          <table
            className="w-full text-left text-sm table-fixed border-collapse h-full"
            role="table"
          style={{ minWidth: `${tableMinWidth}px` }}
          >
            <colgroup>{columns.map(c => {
              const wpx = computedWidths[c.key] ?? getWidthForKey(c.key);
              return <col key={c.key} style={{ width: `${wpx}px` }} />
            })}</colgroup>
            <thead className="text-[11px] text-zinc-600 dark:text-zinc-400 uppercase bg-zinc-50 dark:bg-zinc-800/50">
              {/* Only a bottom border; outer container provides the top/side stroke */}
              <tr className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800">
                {columns.map((c, idx) => (
                  <HeaderCell
                    key={c.key}
                    c={c}
                    idx={idx}
                    visibleOrder={renderedColumnKeys}
                    onShift={handleColumnShift}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedTrades.map((t, i) => (
                <tr
                  key={t.id}
                  className={`${i % 2 === 0 ? 'bg-white dark:bg-zinc-900' : 'bg-zinc-50/60 dark:bg-zinc-900/40'} hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70 transition-colors border-b border-zinc-200 dark:border-zinc-800`}
                >
                  {columns.map((c, ci) => (
                    <td
                      key={c.key}
                      role="cell"
                      className={`py-2.5 px-4 whitespace-nowrap overflow-hidden text-ellipsis ${c.isNumeric ? 'tabular-nums' : ''} ${c.align==='right'? 'text-right':c.align==='center'? 'text-center':'text-left'} ${ci < columns.length - 1 ? 'border-r border-zinc-200 dark:border-zinc-800' : ''}`}
                      style={{
                        transition: 'opacity 120ms ease',
                        width: computedWidths[c.key] ?? getWidthForKey(c.key),
                        minWidth: getWidthForKey(c.key),
                      }}
                    >
                      {c.render ? c.render(t) : (t as any)[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            </table>
          ) : (
            <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-zinc-500 dark:text-zinc-400">
              Import Trades to see the widget
            </div>
          )}
        </div>
      <div className="mt-3 flex flex-shrink-0 flex-wrap items-center justify-between gap-3 px-1">
        <p className="text-xs text-zinc-500 dark:text-zinc-400">{tradingDateCaption}</p>
        {pageCount > 1 && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-35 dark:text-zinc-400 dark:hover:text-zinc-100"
              aria-label="Previous recent trades page"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <span className="tabular-nums">
              Page {currentPage + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
              disabled={currentPage >= pageCount - 1}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-35 dark:text-zinc-400 dark:hover:text-zinc-100"
              aria-label="Next recent trades page"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        )}
        <a
          href="/trades"
          onClick={(event) => {
            const navigate = (window as Window & { __TT_navigate?: (page: string) => void }).__TT_navigate;
            if (!navigate) return;
            event.preventDefault();
            navigate('Trades');
          }}
          className="inline-flex min-h-10 items-center rounded-md px-2 text-sm font-semibold text-sky-500 transition-colors hover:text-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
        >
          <span>View all trades</span>
          <ChevronsRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
};
